import { CalendarBlankIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { Checkbox, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { format, isBefore, isValid, parseISO, startOfMonth } from "date-fns";
import { fr } from "date-fns/locale";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

type DateFieldProps = {
  startDate?: string;
  endDate?: string;
  isPresent?: boolean;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onPresentChange: (value: boolean) => void;
  onError?: (hasError: boolean) => void;
  label?: string;
  presentLabel?: string;
  className?: string;
  fromYear?: number;
  toYear?: number;
};

type MonthYearPickerProps = {
  value?: Date;
  minDate?: Date;
  fromYear: number;
  toYear: number;
  onSelect: (date: Date) => void;
};

const MONTHS = Array.from({ length: 12 }, (_, month) => {
  const date = new Date(2024, month, 1);

  return {
    value: month,
    shortLabel: format(date, "MMM", { locale: fr }),
    fullLabel: format(date, "MMMM", { locale: fr }),
  };
});

const toDate = (value?: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  /*
   * Les anciennes valeurs YYYY-MM-DD
   * restent entièrement compatibles.
   * Le format YYYY-MM est également accepté.
   */
  const normalizedValue = value.length === 7 ? `${value}-01` : value;

  const date = parseISO(normalizedValue);

  return isValid(date) ? startOfMonth(date) : undefined;
};

const toString = (date?: Date): string => {
  if (!date || !isValid(date)) {
    return "";
  }

  /*
   * On conserve le format attendu par
   * les données existantes du CV.
   */
  return format(startOfMonth(date), "yyyy-MM-dd");
};

const formatDisplay = (value?: string): string => {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  const formatted = format(date, "MMMM yyyy", {
    locale: fr,
  });

  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const normalizeMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

const MonthYearPicker = ({ value, minDate, fromYear, toYear, onSelect }: MonthYearPickerProps) => {
  const normalizedMinimum = minDate ? normalizeMonth(minDate) : undefined;

  const minimumYear = normalizedMinimum
    ? Math.max(fromYear, normalizedMinimum.getFullYear())
    : fromYear;

  const maximumYear = Math.max(minimumYear, toYear);

  const initialYear = Math.min(
    maximumYear,
    Math.max(
      minimumYear,
      value?.getFullYear() ?? normalizedMinimum?.getFullYear() ?? new Date().getFullYear(),
    ),
  );

  const [displayedYear, setDisplayedYear] = useState(initialYear);

  useEffect(() => {
    setDisplayedYear(initialYear);
  }, [initialYear]);

  const years = useMemo(
    () =>
      Array.from(
        {
          length: maximumYear - minimumYear + 1,
        },
        (_, index) => maximumYear - index,
      ),
    [maximumYear, minimumYear],
  );

  const selectedMonth = value?.getMonth();
  const selectedYear = value?.getFullYear();

  const canGoPrevious = displayedYear > minimumYear;

  const canGoNext = displayedYear < maximumYear;

  const isMonthDisabled = (month: number) => {
    const candidate = new Date(displayedYear, month, 1);

    if (normalizedMinimum && isBefore(candidate, normalizedMinimum)) {
      return true;
    }

    return candidate.getFullYear() < fromYear || candidate.getFullYear() > toYear;
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <Button
          type="button"
          variant="ghost"
          disabled={!canGoPrevious}
          onClick={() => {
            setDisplayedYear((year) => Math.max(minimumYear, year - 1));
          }}
          aria-label="Année précédente"
          className="size-9 shrink-0 rounded-xl p-0 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-30"
        >
          <CaretLeftIcon size={16} weight="bold" />
        </Button>

        <div className="relative min-w-0 flex-1">
          <select
            value={displayedYear}
            onChange={(event) => {
              setDisplayedYear(Number(event.target.value));
            }}
            aria-label="Sélectionner une année"
            className={cn(
              "h-9 w-full cursor-pointer appearance-none",
              "rounded-xl border border-indigo-100",
              "bg-indigo-50/70 px-3 text-center",
              "text-sm font-bold text-indigo-950",
              "outline-none transition",
              "hover:border-indigo-200 hover:bg-indigo-50",
              "focus:border-indigo-400",
              "focus:ring-2 focus:ring-indigo-200",
            )}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <Button
          type="button"
          variant="ghost"
          disabled={!canGoNext}
          onClick={() => {
            setDisplayedYear((year) => Math.min(maximumYear, year + 1));
          }}
          aria-label="Année suivante"
          className="size-9 shrink-0 rounded-xl p-0 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-30"
        >
          <CaretRightIcon size={16} weight="bold" />
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-3">
        {MONTHS.map((month) => {
          const disabled = isMonthDisabled(month.value);

          const selected = selectedYear === displayedYear && selectedMonth === month.value;

          return (
            <button
              key={month.value}
              type="button"
              disabled={disabled}
              title={month.fullLabel}
              onClick={() => {
                onSelect(new Date(displayedYear, month.value, 1));
              }}
              className={cn(
                "min-h-10 rounded-xl border px-2",
                "text-xs font-semibold capitalize",
                "transition-all duration-150",
                "focus-visible:outline-none",
                "focus-visible:ring-2",
                "focus-visible:ring-indigo-300",
                selected
                  ? ["border-indigo-600", "bg-indigo-600", "text-white", "shadow-sm"]
                  : [
                      "border-slate-100",
                      "bg-white",
                      "text-slate-600",
                      "hover:border-indigo-200",
                      "hover:bg-indigo-50",
                      "hover:text-indigo-700",
                    ],
                disabled && [
                  "cursor-not-allowed",
                  "border-transparent",
                  "bg-slate-50",
                  "text-slate-300",
                  "opacity-60",
                  "hover:border-transparent",
                  "hover:bg-slate-50",
                  "hover:text-slate-300",
                ],
              )}
            >
              {month.shortLabel}
            </button>
          );
        })}
      </div>

      <p className="mt-3 border-t border-slate-100 pt-2 text-center text-[10px] leading-4 text-slate-400">
        Sélectionnez directement le mois et l’année.
      </p>
    </div>
  );
};

export const DateField = ({
  startDate = "",
  endDate = "",
  isPresent = false,
  onStartDateChange,
  onEndDateChange,
  onPresentChange,
  onError,
  label = "Période",
  presentLabel = "En cours / Présent",
  className,
  fromYear = 1960,
  toYear = new Date().getFullYear() + 5,
}: DateFieldProps) => {
  const [openStart, setOpenStart] = useState(false);

  const [openEnd, setOpenEnd] = useState(false);

  /*
   * Conserve temporairement la date de fin
   * lorsque l’utilisateur active « En cours ».
   */
  const previousEndDate = useRef(endDate);

  const startDateValue = useMemo(() => toDate(startDate), [startDate]);

  const endDateValue = useMemo(() => toDate(endDate), [endDate]);

  useEffect(() => {
    if (endDate) {
      previousEndDate.current = endDate;
    }
  }, [endDate]);

  const hasError = useMemo(() => {
    if (isPresent || !startDateValue || !endDateValue) {
      return false;
    }

    return isBefore(endDateValue, startDateValue);
  }, [endDateValue, isPresent, startDateValue]);

  useEffect(() => {
    onError?.(hasError);
  }, [hasError, onError]);

  const handleStartSelect = (date: Date) => {
    const value = toString(date);

    onStartDateChange(value);
    setOpenStart(false);

    /*
     * Si la nouvelle date de début dépasse
     * la date de fin, la fin est supprimée.
     */
    if (!isPresent && endDateValue && isBefore(endDateValue, date)) {
      previousEndDate.current = "";
      onEndDateChange("");
    }
  };

  const handleEndSelect = (date: Date) => {
    if (startDateValue && isBefore(date, startDateValue)) {
      return;
    }

    const value = toString(date);

    previousEndDate.current = value;
    onEndDateChange(value);
    setOpenEnd(false);
  };

  const handlePresentChange = (checked: boolean | "indeterminate") => {
    const nextValue = checked === true;

    if (nextValue) {
      if (endDate) {
        previousEndDate.current = endDate;
      }

      onEndDateChange("");
      setOpenEnd(false);
    } else {
      const previousDate = toDate(previousEndDate.current);

      /*
       * L’ancienne date n’est restaurée que
       * si elle reste cohérente avec le début.
       */
      if (previousDate && (!startDateValue || !isBefore(previousDate, startDateValue))) {
        onEndDateChange(previousEndDate.current);
      }
    }

    onPresentChange(nextValue);
  };

  const endError = hasError ? "La date de fin ne peut pas être antérieure à la date de début." : "";

  const endDisabled = isPresent || !startDateValue;

  return (
    <div className={cn("space-y-2", className)}>
      <Label className="text-[11px] font-bold text-indigo-950">{label}</Label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Date de début */}
        <div className="space-y-1">
          <span className="text-[10px] font-semibold text-slate-500">Début</span>

          <Popover
            open={openStart}
            onOpenChange={(open) => {
              setOpenStart(open);

              if (open) {
                setOpenEnd(false);
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-label="Sélectionner la date de début"
                className={cn(
                  "h-10 w-full justify-start",
                  "overflow-hidden rounded-xl",
                  "border-slate-200 bg-white px-3",
                  "text-left text-xs font-normal",
                  "shadow-none transition-colors",
                  "hover:border-indigo-300",
                  "hover:bg-indigo-50/40",
                  "focus-visible:border-indigo-400",
                  "focus-visible:ring-2",
                  "focus-visible:ring-indigo-200",
                  !startDate && "text-slate-400",
                )}
              >
                <CalendarBlankIcon
                  aria-hidden
                  size={16}
                  className="mr-2 shrink-0 text-indigo-500"
                />

                <span className="min-w-0 truncate">
                  {startDate ? formatDisplay(startDate) : "Choisir le mois"}
                </span>
              </Button>
            </PopoverTrigger>

            <PopoverContent
              align="start"
              sideOffset={8}
              collisionPadding={12}
              className={cn(
                "z-[100] p-3",
                "w-[min(20rem,calc(100vw-1.5rem))]",
                "rounded-2xl",
                "border border-indigo-100",
                "bg-white shadow-xl",
              )}
            >
              <MonthYearPicker
                value={startDateValue}
                fromYear={fromYear}
                toYear={toYear}
                onSelect={handleStartSelect}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Date de fin */}
        <div className="space-y-1">
          <span className="text-[10px] font-semibold text-slate-500">Fin</span>

          <Popover
            open={openEnd && !endDisabled}
            onOpenChange={(open) => {
              if (!endDisabled) {
                setOpenEnd(open);

                if (open) {
                  setOpenStart(false);
                }
              }
            }}
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={endDisabled}
                aria-invalid={hasError}
                aria-label="Sélectionner la date de fin"
                className={cn(
                  "h-10 w-full justify-start",
                  "overflow-hidden rounded-xl",
                  "border-slate-200 bg-white px-3",
                  "text-left text-xs font-normal",
                  "shadow-none transition-colors",
                  "hover:border-indigo-300",
                  "hover:bg-indigo-50/40",
                  "focus-visible:border-indigo-400",
                  "focus-visible:ring-2",
                  "focus-visible:ring-indigo-200",
                  !endDate && !isPresent && "text-slate-400",
                  hasError && "border-rose-400 focus-visible:ring-rose-200",
                  endDisabled && "cursor-not-allowed bg-slate-50 opacity-60",
                )}
              >
                <CalendarBlankIcon
                  aria-hidden
                  size={16}
                  className={cn(
                    "mr-2 shrink-0",
                    isPresent ? "text-emerald-500" : "text-indigo-500",
                  )}
                />

                <span className="min-w-0 truncate">
                  {isPresent
                    ? "Aujourd’hui"
                    : endDate
                      ? formatDisplay(endDate)
                      : startDate
                        ? "Choisir le mois"
                        : "Choisir d’abord le début"}
                </span>
              </Button>
            </PopoverTrigger>

            <PopoverContent
              align="end"
              sideOffset={8}
              collisionPadding={12}
              className={cn(
                "z-[100] p-3",
                "w-[min(20rem,calc(100vw-1.5rem))]",
                "rounded-2xl",
                "border border-indigo-100",
                "bg-white shadow-xl",
              )}
            >
              <MonthYearPicker
                value={endDateValue}
                minDate={startDateValue}
                fromYear={fromYear}
                toYear={toYear}
                onSelect={handleEndSelect}
              />
            </PopoverContent>
          </Popover>

          {endError && (
            <span role="alert" className="block text-[10px] leading-4 text-rose-600">
              {endError}
            </span>
          )}
        </div>
      </div>

      <label
        className={cn(
          "flex cursor-pointer select-none",
          "items-center gap-2",
          "rounded-xl border border-transparent",
          "px-1 py-1 transition-colors",
          "hover:bg-indigo-50/60",
        )}
      >
        <Checkbox checked={isPresent} onCheckedChange={handlePresentChange} />

        <span className="text-[11px] font-medium text-slate-600">{presentLabel}</span>
      </label>
    </div>
  );
};
